import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { identity } from 'rxjs';
import { API, ApiKeyDTO } from '../../../services/api/api';
import { ErrorService } from '../../../services/error.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { UtilService } from '../../../services/util.service';
import { UserService } from '../../../services/user.service';
import { TagService } from '../../../services/tag.service';
import { DiscussionService } from '../../../services/discussion.service';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { RequestClass } from '../../../services/global/RequestClass';

type SplitButtonAction = 'edit' | 'expire' | 'delete';

@Component({
  selector: 'sb-page-api-key-list',
  templateUrl: './api-key-list.component.html',
  styleUrls: ['./api-key-list.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApiKeyListComponent extends RequestClass {
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  public readonly translate = inject(TranslateService);
  public readonly userService = inject(UserService);
  public readonly tagService = inject(TagService);
  public readonly discussionService = inject(DiscussionService);
  public readonly permissionService = inject(PermissionService);
  private readonly api = inject(API);
  private readonly modalService = inject(ModalService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  // global variables
  showSideBar: boolean = false;

  // table wrapper variables
  apiKeys: ApiKeyDTO[] = [];
  selectedApiKey: ApiKeyDTO = null;

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .ca_am_ApiKey_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe((data: ApiKeyDTO[]) => {
        this.apiKeys = this.buildSplitButtons(data);
      });
    this.subs.push(sub);
  }

  editApiKey(apiKey: ApiKeyDTO) {
    if (!apiKey) {
      throw Error('No Dataprovided');
    }
    this.showSideBar = false;
    this.requestDebounced();
  }

  selectApiKey(apiKey: ApiKeyDTO) {
    if (!apiKey) {
      return;
    }
    this.selectedApiKey = apiKey;
    this.showSideBar = true;
  }

  toggleSideBar() {
    this.showSideBar = !this.showSideBar;
  }

  deleteApiKey(apiKey: ApiKeyDTO) {
    if (!apiKey) {
      return;
    }
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.deleteElementValue', { value: apiKey.name ?? 'Api Key' }),
        message: this.translate.instant('lang.globals.deleteElementValueQuestion', { value: apiKey.name ?? 'Api Key' }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api.ca_am_ApiKey_delete(apiKey.identifier).subscribe({
            next: () => {
              this.requestDebounced();
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
        }
      })
      .catch(() => {});
  }

  identification(data: any) {
    return identity<ApiKeyDTO>(data);
  }

  setExpired(apiKey: ApiKeyDTO) {
    this.api.ca_am_ApiKey_expire(apiKey.identifier).subscribe({
      next: data => {
        apiKey = data;
        this.requestDebounced();
      },
    });
  }

  buildSplitButtons<T>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      item.sbButtons.push({
        icon: 'edit',
        tooltip: 'lang.globals.edit',
        action: 'edit',
      });
      item.sbButtons.push({
        icon: 'clock',
        tooltip: 'lang.globals.setExpired',
        action: 'expire',
      });
      item.sbButtons.push({
        icon: 'trash',
        iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
        action: 'delete',
        tooltip: 'lang.globals.delete',
      });
    });
    return result;
  }

  resolveSplitButtonAction(apiKey: ApiKeyDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'edit':
        this.selectApiKey(apiKey);
        break;
      case 'expire':
        this.setExpired(apiKey);
        break;
      case 'delete':
        this.deleteApiKey(apiKey);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
